import { Link } from "@tanstack/react-router";
import { ArrowLeft, ChevronsUpDown, GraduationCap, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { campusYearInFY, currentFiscalYear } from "@/lib/fiscal";
import { cn } from "@/lib/utils";

const CURRENT_FY = currentFiscalYear();

interface Campus {
  id: string;
  name: string;
  startDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export function CampusesPage({
  fromOwner,
  ownerName,
  tab,
}: {
  fromOwner?: string;
  ownerName?: string;
  tab?: string;
}) {
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [allCampusNames, setAllCampusNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  const fetchCampuses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/campuses");
      if (res.ok) {
        const data = await res.json();
        setCampuses(data.campuses);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllCampusNames = useCallback(async () => {
    const res = await fetch("/api/deal-values/campuses");
    if (res.ok) {
      const data = await res.json();
      setAllCampusNames(data.values ?? []);
    }
  }, []);

  useEffect(() => {
    fetchCampuses();
    fetchAllCampusNames();
  }, [fetchCampuses, fetchAllCampusNames]);

  function flashSaved(id: string) {
    clearTimeout(savedTimerRef.current);
    setSavedId(id);
    savedTimerRef.current = setTimeout(() => setSavedId(null), 800);
  }

  async function addCampus(name: string) {
    const res = await fetch("/api/campuses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      const data = await res.json();
      setCampuses((prev) => [...prev, data.campus].sort((a, b) => a.name.localeCompare(b.name)));
      flashSaved(data.campus.id);
    }
  }

  async function updateStartDate(id: string, startDate: string | null) {
    const res = await fetch(`/api/campuses/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate || null }),
    });
    if (res.ok) {
      const data = await res.json();
      setCampuses((prev) => prev.map((c) => (c.id === id ? data.campus : c)));
      flashSaved(id);
    }
  }

  async function deleteCampus(id: string) {
    const res = await fetch(`/api/campuses/${id}`, { method: "DELETE" });
    if (res.ok) {
      setCampuses((prev) => prev.filter((c) => c.id !== id));
    }
  }

  const registeredNames = new Set(campuses.map((c) => c.name));
  const availableToAdd = allCampusNames.filter((n) => !registeredNames.has(n));

  const savedBorder = "border-green-500 ring-[3px] ring-green-500/20";

  return (
    <div className="space-y-6">
      {fromOwner && (
        <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
          <Link to="/owner-detail/$ownerId" params={{ ownerId: fromOwner }} search={{ tab }}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to {ownerName ? `${ownerName}'s` : "Owner"} Profile
          </Link>
        </Button>
      )}
      <PageHeader title="Campuses" description="Manage campus registry and start dates." />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
            Campus Registry
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-2">Loading...</p>
          ) : campuses.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No campuses registered yet. Add one below.
            </p>
          ) : (
            <div className="space-y-2">
              {campuses.map((c) => (
                <CampusRow
                  key={c.id}
                  campus={c}
                  isSaved={savedId === c.id}
                  savedBorder={savedBorder}
                  onUpdateStartDate={(sd) => updateStartDate(c.id, sd)}
                  onDelete={() => deleteCampus(c.id)}
                />
              ))}
            </div>
          )}

          {availableToAdd.length > 0 && (
            <div className="mt-4">
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[280px] h-9 justify-between text-sm text-muted-foreground"
                  >
                    <span className="flex items-center gap-1.5">
                      <Plus className="h-3.5 w-3.5" />
                      Add Campus
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search campuses..." />
                    <CommandList>
                      <CommandEmpty>No campus found.</CommandEmpty>
                      {availableToAdd.map((name) => (
                        <CommandItem
                          key={name}
                          value={name}
                          onSelect={() => {
                            addCampus(name);
                            setComboboxOpen(false);
                          }}
                        >
                          {name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CampusRow({
  campus,
  isSaved,
  savedBorder,
  onUpdateStartDate,
  onDelete,
}: {
  campus: Campus;
  isSaved: boolean;
  savedBorder: string;
  onUpdateStartDate: (startDate: string | null) => void;
  onDelete: () => void;
}) {
  const [localDate, setLocalDate] = useState(campus.startDate ?? "");

  // Sync when campus prop changes
  useEffect(() => {
    setLocalDate(campus.startDate ?? "");
  }, [campus.startDate]);

  function handleBlur() {
    const newVal = localDate || null;
    if (newVal !== campus.startDate) {
      onUpdateStartDate(newVal);
    }
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md border px-3 py-2 transition-[border-color,box-shadow] duration-300",
        isSaved && savedBorder,
      )}
    >
      <div className="flex items-center gap-2 flex-1">
        <span className="text-sm font-medium">{campus.name}</span>
        {(() => {
          const year = campusYearInFY(localDate || null, CURRENT_FY);
          if (year !== null) {
            return (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                Year {year}
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
              Year ?
            </Badge>
          );
        })()}
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: label visually associated with adjacent input */}
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Campus Start Date
        </label>
        <Input
          type="date"
          value={localDate}
          onChange={(e) => setLocalDate(e.target.value)}
          onBlur={handleBlur}
          className="w-[160px] h-8 text-sm"
          placeholder="Start date"
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
